import useFetch from "v4/hooks/useFetch";
import {generateUrl} from "v4/services/api.service";
import {LOGIN} from "v4/data/apiRoutes";
import {useEffect, useState} from "react";
import {useAuthContext} from "v4/contexts/AuthContext";
import {useTranslation} from "react-i18next";
import {useHistory} from "react-router-dom";
import {routesBase} from "v4/data/appRoutes";
import jsonToFormData from "v4/utils/jsonToFormData";
import {PASSWORD_TYPE, TEXT_TYPE} from "v4/data/fieldTypes";
import {getLSItem, removeLSItem, setLSItem} from "v4/services/localStorage.service";
import UserService from "api/user/user";

export default function useLogin() {
    const {t} = useTranslation();
    const {setToken} = useAuthContext();
    const {replace, location} = useHistory();
    const [{data, isLoading, errorData, rawResponse}, loginFetch] = useFetch();

    const [isLocked, setIsLocked] = useState(() => {
        const lockInfos = getLSItem('loginLockInfos');

        if (!lockInfos) return false;

        return lockInfos.lockAt + lockInfos.retryAfter * 1000 > Date.now();
    });

    const [errorMessage, setErrorMessage] = useState(() => {
        const lockInfos = getLSItem('loginLockInfos');

        if (!isLocked) return false;

        const retryAfter = Math.ceil((lockInfos.lockAt + lockInfos.retryAfter * 1000 - Date.now()) / 1000);
        return t(lockInfos.message, {retryAfter});
    });

    const onSubmit = (data) => {
        loginFetch({
            url: process.env.REACT_APP_POL_CORE_URL + generateUrl(LOGIN, {}, false),
            config: {
                method: 'POST',
                body: jsonToFormData(data),
                credentials: 'include', // Include cookies for session management
            }
        });
    }

    useEffect(() => {
        if (!get2FAValidity().isValid) return;

        replace({
            pathname: routesBase.baseLogin2FA,
            search: location.search,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (data?.type === "success:login-two-factor-required") {
            setLSItem('login2FALastTry', Date.now());

            replace({
                pathname: routesBase.baseLogin2FA,
                search: location.search,
            });

            return;
        }

        if (!data?.data || !data?.data?.auth_token) return;

        if (data.data.redirect_url) {
            UserService.setRedirectTo(data.data.redirect_url);
        }

        setToken(data.data.auth_token);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const form = {
        type: "success:get-form-schema",
        data: {
            "login[username]": {
                attr: {
                    autocomplete: "username"
                },
                constraints: {
                    required: true,
                },
                label: t('login_or_mail_address'),
                name: "username",
                type: TEXT_TYPE,
            },
            "login[password]": {
                attr: {
                    "autocomplete": "current-password"
                },
                constraints: {
                    required: true,
                },
                label: t('mot_passe'),
                name: "password",
                type: PASSWORD_TYPE,
            },
        }
    }

    useEffect(() => {
        if (!errorData || ![401, 429].includes(errorData.status) || errorData.bodyUsed) return;

        const retryAfterHeader = rawResponse.headers.get('Retry-After');

        const {type} = errorData;
        const isTooManyLoginFailure = type === 'client-error:too-many-requests';

        setErrorMessage(t(isTooManyLoginFailure ? 'ip_locked' : 'invalid_credentials', {retryAfter: retryAfterHeader}));
        setIsLocked(isTooManyLoginFailure);

        if (isTooManyLoginFailure) {
            setLSItem('loginLockInfos', {
                lockAt: Date.now(),
                retryAfter: retryAfterHeader,
                message: 'ip_locked'
            });
            setTimeout(() => {
                setIsLocked(false);
                setErrorMessage(undefined);
                removeLSItem('loginLockInfos')
            }, retryAfterHeader * 1000);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errorData]);

    return {
        form,
        formErrorMessage: errorMessage ?? errorData?.details,
        isLocked,
        onSubmit,
        isLoading,
    }
}


export function get2FAValidity() {
    const login2FALastTry = getLSItem('login2FALastTry', 'string');

    if (!login2FALastTry) {
        return {
            isValid: false,
            remainingTime: 0
        }
    }

    const lastTry = parseInt(login2FALastTry, 10);
    const authorizedTime = 4 * 60 * 1000 + 30 * 1000; // 4 minutes and 30 seconds
    const now = Date.now();
    const elapsedTime = now - lastTry;

    return {
        isValid: elapsedTime < authorizedTime,
        remainingTime: authorizedTime - elapsedTime
    }
}
